import { useEffect, useState } from 'react';

import { parseDate } from '@datepicker-react/hooks';

import { hotelItemsTOStringify } from '@components/views/landing/components/booker/common/bookerUtils';
import { HOTEL_PAST_SEARCH } from '@constants/constants';
import { isDateOver } from '@utils/dateUtil';
import { formatDate } from '@domain/hotel/searchBar/utils';
import { HotelSearchViewModel } from '@api/index';
import { BookerSearchViewModel } from '@components/views/landing/components/booker/common/BookerSearchDrawer';

/**
 * Custom hook to manage past searches from the hotel
 */
export const useHotelPastSearches = () => {
  const [pastHotelItems, setPastHotelItems] = useState<any[]>([]);

  /**
   * Read past search items from local storage
   * Remove outdated items from the list
   * Update the local storage items
   */
  useEffect(() => {
    const pastItems =
      typeof window !== 'undefined' && localStorage.getItem(HOTEL_PAST_SEARCH)
        ? JSON.parse(localStorage.getItem(HOTEL_PAST_SEARCH) || '')
        : [];

    const filteredItem = pastItems.filter(
      (item: any) =>
        !isDateOver(parseDate(item.in, String(`yyyy-MM-dd`), new Date()))
    );

    if (typeof window !== 'undefined')
      localStorage.setItem(
        HOTEL_PAST_SEARCH,
        JSON.stringify(filteredItem, hotelItemsTOStringify)
      );

    setPastHotelItems(filteredItem);
  }, []);

  /**
   * Handle save items to local storage
   */
  const saveHotelSearch = ({ dataHotel, dataDate, hotelRooms }) => {
    let hotelItems: HotelSearchViewModel[] = [];

    hotelItems = pastHotelItems ? [...pastHotelItems] : [];

    const newSearch = {
      ...dataHotel,
      slug: dataHotel?.url,
      in: formatDate(dataDate?.startDate),
      out: formatDate(dataDate?.endDate),
      rooms: hotelRooms,
    };

    if (!hotelItems.find((i) => i.url === dataHotel?.url)) {
      hotelItems.unshift(newSearch as HotelSearchViewModel);
    } else {
      const target = hotelItems.find((i) => i.url === dataHotel?.url);

      const targetIndex = target ? hotelItems.indexOf(target) : -1;

      hotelItems.splice(targetIndex, 1);
      hotelItems.unshift(newSearch as HotelSearchViewModel);
    }

    if (typeof window !== 'undefined') {
      localStorage.setItem(
        HOTEL_PAST_SEARCH,
        JSON.stringify(hotelItems, hotelItemsTOStringify)
      );
      setPastHotelItems(hotelItems);
    }
  };

  /**
   * Clear past search history items
   */
  const clearPastHotelSearch = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(HOTEL_PAST_SEARCH);
      setPastHotelItems([]);
    }
  };

  /**
   * Remove one item from local storage
   */
  const removePastHotelSearch = (item: BookerSearchViewModel) => {
    const newItems = pastHotelItems?.filter(
      (i: BookerSearchViewModel) => i.text !== item.text
    );

    setPastHotelItems(newItems);

    if (typeof window !== 'undefined')
      localStorage.setItem(
        HOTEL_PAST_SEARCH,
        JSON.stringify(newItems, hotelItemsTOStringify)
      );
  };

  return {
    pastHotelItems,
    saveHotelSearch,
    clearPastHotelSearch,
    removePastHotelSearch,
  };
};
